/**
 * Demo Simulation — Branch Trust Package (Mrs Dlamini's story).
 *
 * A step-by-step walkthrough of the branch-time issuance flow:
 *   1. Consumer visits the branch
 *   2. Adviser generates the quote
 *   3. Institution calls POST /api/trust-package
 *   4. Trust Package returns → printed on the quote (SVG badge)
 *   5. Adviser hands over via 4 channels
 *   6. Consumer completes the journey → Intelligence Summary flows back
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { WalletShell, NAVY, GOLD } from "@/components/wallet/WalletShell";
import {
  Building2, User, Sparkles, ArrowRight, ArrowLeft, Loader2, QrCode,
  MessageSquare, Send, Monitor, FileText, ShieldCheck, ExternalLink, RefreshCcw,
  CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}`;

const STEPS = [
  { n: 1, key: "branch", title: "Mrs Dlamini visits the branch", body: "She'd like funeral cover. The adviser runs the normal needs analysis. No Altara interaction yet." },
  { n: 2, key: "quote", title: "Quote generated in the bank's system", body: "The adviser produces the quote inside ABC Bank's existing platform." },
  { n: 3, key: "package", title: "Altara returns the Trust Package", body: "The institution calls POST /api/trust-package. Altara returns a Trust Certificate, QR code, secure link, badge, journey ID and verification token." },
  { n: 4, key: "printed", title: "Quote printed with the Trust Badge", body: "The printable badge is embedded at the bottom of the quotation." },
  { n: 5, key: "handoff", title: "Adviser hands over the journey", body: "Four channels — QR, SMS, WhatsApp/RCS, or assisted branch tablet. No consumer is excluded." },
  { n: 6, key: "feedback", title: "Consumer Intelligence Summary flows back", body: "Once the consumer completes the journey, the institution receives structured signals — never raw answers." },
];


export default function WalletBranchTrustPackageDemo() {
  const [step, setStep] = useState(1);
  const [pkg, setPkg] = useState(null);
  const [intel, setIntel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const generatePackage = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/trust-package`, {
        institution_name: "ABC Bank",
        advisor_name: "Nandi Molefe",
        consumer_full_name: "Mrs Nomsa Dlamini",
        consumer_msisdn: "+27821234567",
        product_type: "funeral_cover",
        product_name: "ABC Family Funeral Plan · R30 000",
        monthly_premium: 215.00,
        sum_assured: 30000,
      });
      setPkg(data);
      setStep(3);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to generate Trust Package");
    } finally { setBusy(false); }
  };

  const fetchIntelligence = async () => {
    if (!pkg) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/trust-package/${pkg.journey_id}/intelligence`);
      setIntel(data);
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  const reset = () => { setPkg(null); setIntel(null); setStep(1); setErr(""); };

  return (
    <WalletShell activeTab="demos">
      <div className="max-w-5xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-6" data-testid="branch-trust-demo">
        <header>
          <Link to="/wallet/demos" className="text-[11px] text-slate-500 hover:underline inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to Demo Simulation</Link>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Branch scenario · Live simulation</div>
            <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>Trust Package — issued at the branch</h1>
            <p className="mt-2 text-sm text-slate-600 max-w-3xl">Walk through Mrs Dlamini&apos;s story: she visits ABC Bank for funeral cover, the adviser generates a quote in the bank&apos;s own system, and one API call turns that quote into a guided <strong style={{ color: NAVY }}>Consumer Trust Journey</strong>. Every artefact below is live infrastructure.</p>
          </div>
        </header>

        {/* Step rail */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1" data-testid="branch-steps">
          {STEPS.map((s, i) => {
            const active = s.n === step, done = s.n < step;
            return (
              <div key={s.n} className="flex items-center gap-1 flex-shrink-0">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: done ? GOLD : active ? NAVY : "#E2E8F0", color: done ? NAVY : active ? "white" : "#94A3B8" }}>
                  {done ? "✓" : s.n}
                </div>
                <span className={`text-[11px] font-medium hidden sm:inline ${active ? "" : "text-slate-500"}`} style={active ? { color: NAVY } : undefined}>{s.title.split(" ")[0]}</span>
                {i < STEPS.length - 1 && <div className="w-6 h-px bg-slate-200 mx-1" />}
              </div>
            );
          })}
        </div>

        {/* Step content */}
        {step === 1 && <BranchStep onNext={() => setStep(2)} />}
        {step === 2 && <QuoteStep onNext={generatePackage} busy={busy} error={err} />}
        {step === 3 && pkg && <PackageStep pkg={pkg} onNext={() => setStep(4)} />}
        {step === 4 && pkg && <PrintedQuoteStep pkg={pkg} onNext={() => setStep(5)} />}
        {step === 5 && pkg && <HandoffStep pkg={pkg} onNext={() => { setStep(6); fetchIntelligence(); }} />}
        {step === 6 && pkg && <IntelligenceStep pkg={pkg} intel={intel} onRefresh={fetchIntelligence} busy={busy} onRestart={reset} />}
      </div>
    </WalletShell>
  );
}


function StepCard({ eyebrow, title, children }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-6 lg:p-8 space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>{eyebrow}</div>
        <h2 className="mt-1 font-display text-xl lg:text-2xl font-semibold" style={{ color: NAVY }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}


function BranchStep({ onNext }) {
  return (
    <StepCard eyebrow="Step 1" title="Mrs Dlamini walks into ABC Bank">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ActorTile icon={User} title="Mrs Nomsa Dlamini" sub="Consumer" rows={[["Reason", "Would like funeral cover"], ["Digital confidence", "Comfortable via WhatsApp"], ["Phone", "+27 82 123 4567"]]} />
        <ActorTile icon={Building2} title="Nandi Molefe" sub="ABC Bank · branch adviser" rows={[["Role", "Bancassurance representative"], ["Branch", "Sandton City"], ["System", "ABC Bank iAdvisor"]]} />
      </div>
      <p className="text-[13px] text-slate-600 leading-relaxed">The adviser conducts the standard needs analysis. Nothing about Altara has entered the conversation yet — this is business as usual.</p>
      <NextButton onClick={onNext} label="Adviser generates the quote →" />
    </StepCard>
  );
}


function QuoteStep({ onNext, busy, error }) {
  return (
    <StepCard eyebrow="Step 2" title="The quote is generated inside ABC Bank's own system">
      <div className="rounded-xl border border-slate-200 p-5 bg-slate-50/50 font-mono text-[12px] space-y-1 text-slate-700">
        <div><span className="text-slate-400">product</span> · ABC Family Funeral Plan (R30 000 cover)</div>
        <div><span className="text-slate-400">premium</span> · R 215.00 / month</div>
        <div><span className="text-slate-400">term</span> · Whole-life · immediate cover from month 7</div>
        <div><span className="text-slate-400">quote ref</span> · ABC-2026-0044821</div>
      </div>
      <p className="text-[13px] text-slate-600 leading-relaxed">Now ABC Bank makes ONE simple API call:</p>
      <pre className="rounded-xl bg-slate-900 text-emerald-200 p-4 text-[11px] font-mono overflow-x-auto"><code>{`POST /api/trust-package
{
  "institution_name": "ABC Bank",
  "advisor_name": "Nandi Molefe",
  "consumer_full_name": "Mrs Nomsa Dlamini",
  "consumer_msisdn": "+27821234567",
  "product_type": "funeral_cover",
  "product_name": "ABC Family Funeral Plan · R30 000",
  "monthly_premium": 215.00,
  "sum_assured": 30000
}`}</code></pre>
      {error && <div className="rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800 p-3">{error}</div>}
      <NextButton onClick={onNext} label={busy ? "Generating Trust Package…" : "Call the API →"} busy={busy} highlight />
    </StepCard>
  );
}


function PackageStep({ pkg, onNext }) {
  return (
    <StepCard eyebrow="Step 3" title="Altara returns the Trust Package">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FactBlock label="Journey ID" value={pkg.journey_id} mono testId="pkg-journey-id" />
        <FactBlock label="Verification token" value={pkg.verification_token} mono testId="pkg-verification-token" />
        <FactBlock label="Secure link" value={pkg.secure_link} mono link testId="pkg-secure-link" />
        <FactBlock label="Badge SVG" value={pkg.badge_svg_url} mono link testId="pkg-badge-url" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4 items-start">
        <div className="rounded-2xl border border-slate-100 p-4 bg-white flex items-center justify-center">
          <img src={`${BASE}/api/trust-package/${pkg.journey_id}/qr.svg`} alt="QR" className="w-40 h-40" data-testid="pkg-qr-img" />
        </div>
        <div className="text-[13px] leading-relaxed text-slate-700">
          <p>The QR code encodes the <strong style={{ color: NAVY }}>secure journey link</strong>. Scanning it opens Mrs Dlamini&apos;s Consumer Trust Journey — the same one she&apos;ll see via SMS, WhatsApp or a branch tablet.</p>
          <p className="mt-2">The <strong style={{ color: NAVY }}>quote itself never leaves the institution</strong>. Altara only issues the trust envelope.</p>
        </div>
      </div>
      <NextButton onClick={onNext} label="See the printed quote →" />
    </StepCard>
  );
}


function PrintedQuoteStep({ pkg, onNext }) {
  return (
    <StepCard eyebrow="Step 4" title="The quote prints with the Trust Badge at the bottom">
      {/* Mockup printed quote */}
      <div className="rounded-2xl bg-white shadow-md border border-slate-200 overflow-hidden" data-testid="printed-quote-mockup">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="text-[9px] uppercase tracking-widest text-slate-500">ABC Bank · Family Funeral Plan quotation</div>
          <div className="mt-1 font-display text-lg font-semibold" style={{ color: NAVY }}>Prepared for Mrs Nomsa Dlamini</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Quote ABC-2026-0044821 · issued {new Date(pkg.issued_at).toLocaleDateString()}</div>
        </div>
        <div className="px-6 py-4 text-[12px] text-slate-700 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <span className="text-slate-500">Sum assured</span><span className="font-semibold" style={{ color: NAVY }}>R 30 000</span>
            <span className="text-slate-500">Monthly premium</span><span className="font-semibold" style={{ color: NAVY }}>R 215.00</span>
            <span className="text-slate-500">Cover start</span><span>1st of next month</span>
            <span className="text-slate-500">Waiting period</span><span>6 months (natural causes)</span>
          </div>
          <p className="mt-3 text-[11px] text-slate-500 italic">This quote is valid for 30 days from issue. Standard exclusions apply — see full policy wording.</p>
        </div>
        <div className="border-t border-slate-100 bg-slate-50/60 p-4">
          <img
            src={`${BASE}/api/trust-package/${pkg.journey_id}/badge.svg`}
            alt="Consumer Trust Journey badge"
            className="w-full max-w-xl"
            data-testid="printed-quote-badge"
          />
        </div>
      </div>
      <p className="text-[13px] text-slate-600 leading-relaxed">Instantly the quote is no longer a static document — it&apos;s a guided decision experience. The adviser doesn&apos;t say &ldquo;please read this&rdquo;. They say <em>&ldquo;let&apos;s complete your journey together&rdquo;</em>.</p>
      <NextButton onClick={onNext} label="How the adviser hands it over →" />
    </StepCard>
  );
}


function HandoffStep({ pkg, onNext }) {
  const channels = [
    { icon: QrCode, tone: "gold", title: "Scan QR", body: "Consumer scans on their own phone at the branch. Wallet opens exactly where the journey starts." },
    { icon: MessageSquare, tone: "navy", title: "SMS", body: "Adviser clicks Send Journey. Consumer receives an SMS with the secure link." },
    { icon: Send, tone: "navy", title: "WhatsApp / RCS", body: "Verified message arrives on WhatsApp — same journey, richer preview." },
    { icon: Monitor, tone: "gold", title: "Assisted branch tablet", body: "For consumers who don't own smartphones or need face-to-face support. Adviser rotates the monitor. Same experience, same evidence, same intelligence." },
  ];
  return (
    <StepCard eyebrow="Step 5" title="Four ways to complete the journey — no consumer excluded">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {channels.map(c => <ChannelTile key={c.title} {...c} />)}
      </div>
      <div className="rounded-xl p-4" style={{ background: `${GOLD}12`, border: `1px solid ${GOLD}55` }}>
        <div className="text-[12px] leading-relaxed text-slate-700">
          <strong style={{ color: NAVY }}>Try it now:</strong> open the journey in a new tab as the consumer would:
          {" "}<a href={pkg.secure_link} target="_blank" rel="noreferrer" className="font-semibold underline" style={{ color: NAVY }} data-testid="handoff-open-journey">Open Mrs Dlamini&apos;s journey <ExternalLink className="w-3 h-3 inline" /></a>
        </div>
      </div>
      <NextButton onClick={onNext} label="See what flows back to the institution →" />
    </StepCard>
  );
}


function IntelligenceStep({ pkg, intel, onRefresh, busy, onRestart }) {
  return (
    <StepCard eyebrow="Step 6" title="Consumer Intelligence Summary — flows back to the institution's CRM">
      <p className="text-[13px] text-slate-600 leading-relaxed">Once Mrs Dlamini completes the journey, ABC Bank calls <code className="font-mono text-[11px] bg-slate-100 px-1 py-0.5 rounded">GET /api/trust-package/{pkg.journey_id}/intelligence</code> and receives <strong style={{ color: NAVY }}>structured signals only</strong> — never her raw NAVI answers.</p>
      {!intel ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="intel-grid">
            <IntelRow label="Journey status" value={intel.journey_status} good={intel.journey_status === "completed"} />
            <IntelRow label="Product understanding" value={intel.product_understanding} good={["good","strong"].includes(intel.product_understanding)} />
            <IntelRow label="Decision confidence" value={intel.decision_confidence} good={intel.decision_confidence === "high"} />
            <IntelRow label="Support requested" value={intel.support_requested ? "Yes" : "No"} good={!intel.support_requested} />
            <IntelRow label="Documents read" value={String(intel.documents_read)} />
            <IntelRow label="AI briefs generated" value={String(intel.ai_briefs_generated)} />
            <IntelRow label="Consumer questions" value={String(intel.consumer_questions)} />
            <IntelRow label="Knowledge gap areas" value={intel.knowledge_gap_areas.join(", ") || "None detected"} />
          </div>
          <div className="rounded-xl p-4" style={{ background: "#F1F5F9", border: "1px solid #E2E8F0" }}>
            <div className="text-[11px] text-slate-600 leading-relaxed">{intel.disclaimer}</div>
          </div>
        </>
      )}
      <div className="flex flex-wrap gap-2">
        <button onClick={onRefresh} disabled={busy} data-testid="intel-refresh" className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium border border-slate-200 hover:bg-slate-50 disabled:opacity-40" style={{ color: NAVY }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} Refresh intelligence
        </button>
        <button onClick={onRestart} data-testid="intel-restart" className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold" style={{ background: NAVY, color: "white" }}>
          <Sparkles className="w-4 h-4" /> Run the simulation again
        </button>
      </div>
    </StepCard>
  );
}


// ── Small components ───────────────────────────────────────


function NextButton({ onClick, label, busy, highlight }) {
  return (
    <button onClick={onClick} disabled={busy} data-testid="branch-next" className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold disabled:opacity-40 transition"
      style={highlight ? { background: GOLD, color: NAVY } : { background: NAVY, color: "white" }}>
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{label} <ArrowRight className="w-4 h-4" /></>}
    </button>
  );
}


function ActorTile({ icon: Icon, title, sub, rows }) {
  return (
    <div className="rounded-2xl border border-slate-100 p-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${GOLD}22` }}>
          <Icon className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <div>
          <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{title}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{sub}</div>
        </div>
      </div>
      <div className="mt-3 space-y-1">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[110px_1fr] gap-3 text-[11px]">
            <span className="text-slate-500 uppercase tracking-widest text-[9px]">{k}</span>
            <span className="text-slate-700">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function FactBlock({ label, value, mono, link, testId }) {
  return (
    <div className="rounded-lg p-3 bg-slate-50 border border-slate-100" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-widest text-slate-500">{label}</div>
      {link ? (
        <a href={value} target="_blank" rel="noreferrer" className={`text-[11px] break-all hover:underline ${mono ? "font-mono" : ""}`} style={{ color: NAVY }}>{value}</a>
      ) : (
        <div className={`text-[11px] break-all ${mono ? "font-mono" : ""}`} style={{ color: NAVY }}>{value}</div>
      )}
    </div>
  );
}


function ChannelTile({ icon: Icon, tone, title, body }) {
  const bg = tone === "gold" ? `${GOLD}22` : `${NAVY}12`;
  return (
    <div className="rounded-2xl border border-slate-100 p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
        <Icon className="w-4 h-4" style={{ color: NAVY }} />
      </div>
      <div>
        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{title}</div>
        <div className="mt-1 text-[12px] leading-relaxed text-slate-600">{body}</div>
      </div>
    </div>
  );
}


function IntelRow({ label, value, good }) {
  return (
    <div className="rounded-lg border border-slate-100 p-3 flex items-center justify-between gap-3">
      <span className="text-[10px] uppercase tracking-widest text-slate-500">{label}</span>
      <span className="text-[13px] font-semibold flex items-center gap-1.5" style={{ color: good ? "#065F46" : NAVY }}>
        {good && <CheckCircle2 className="w-3.5 h-3.5" />}
        {value}
      </span>
    </div>
  );
}
